import { AdFrame } from '@generated/components/AdEdits';
import { useEffect, useRef, useState } from 'react';
import { toPng } from 'html-to-image';
import { Download, Loader2, Star } from 'lucide-react';

const ART = 'https://files-public.monday.com/use1/e82ffcb5-4e45-49ff-a00d-170dc00735d4/generated-bold-retro-screen-printed-illustration-o-406f068a.jpg';
const PRICES = [['Quarter', 300], ['Half', 500], ['Full', 850]] as const;

type Format = { id: string; label: string; note: string; w: number; h: number; out: string };
const FORMATS: Format[] = [
  { id: 'instagram', label: 'Instagram post', note: 'Square', w: 540, h: 540, out: '1080 × 1080' },
  { id: 'facebook', label: 'Facebook post / link image', note: 'Wide', w: 600, h: 315, out: '1200 × 630' },
  { id: 'email', label: 'Email banner', note: 'Fits a standard 600-wide email', w: 600, h: 760, out: '1200 × 1520' },
];

function Prices({ size = 'md' }: { size?: 'sm' | 'md' }) {
  return (
    <div className="grid grid-cols-3 border-4 border-foreground bg-card">
      {PRICES.map(([s, p], i) => (
        <div key={s} className={`py-2 text-center ${i ? 'border-l-4 border-foreground' : ''}`}>
          <div className={`${size === 'sm' ? 'text-[10px]' : 'text-[12px]'} font-black uppercase italic leading-tight`}>Two {s}<br />Page Ads</div>
          <div className={`${size === 'sm' ? 'text-[26px]' : 'text-[38px]'} font-black italic leading-none text-primary`}>${p}</div>
        </div>
      ))}
    </div>
  );
}
const Tag = () => <span className="inline-flex items-center gap-1 border-2 border-foreground bg-[hsl(var(--brand-yellow))] px-2 py-0.5 text-[10px] font-black uppercase tracking-[0.18em]"><Star className="h-3 w-3 fill-current" />Star-Revue twofer</span>;
const Foot = ({ small = false }: { small?: boolean }) => <div className={`bg-[hsl(var(--brand-blue))] px-4 ${small ? 'py-1.5 text-[10px]' : 'py-2.5 text-[12px]'} font-bold text-primary-foreground`}>One ad in November + one in December · All ads also run on our website · Call 917-652-9128</div>;

function Ad({ f, art }: { f: Format; art: string }) {
  if (f.id === 'facebook') return (
    <div className="flex h-full w-full flex-col border-[6px] border-foreground bg-card font-sans">
      <div className="flex min-h-0 flex-1">
        <div className="flex min-w-0 flex-1 flex-col justify-between p-4">
          <div><Tag /><div className="mt-2 text-[30px] font-black uppercase leading-[0.92] tracking-tight text-primary">Special end-of-year ad deals!</div>
            <div className="mt-1.5 text-[12px] font-bold">Thanksgiving + the Holiday Season + the Red Hook Stars winners</div></div>
          <Prices size="sm" />
        </div>
        <img src={art} data-src={ART} alt="" className="w-[190px] border-l-[6px] border-foreground object-cover" />
      </div>
      <Foot small />
    </div>
  );
  const tall = f.id === 'email';
  return (
    <div className="flex h-full w-full flex-col border-[8px] border-foreground bg-card font-sans">
      <div className={`relative ${tall ? 'h-[300px]' : 'h-[190px]'} border-b-[6px] border-foreground`}>
        <img src={art} data-src={ART} alt="" className="h-full w-full object-cover" />
        <div className="absolute left-4 top-4"><Tag /></div>
      </div>
      <div className="flex flex-1 flex-col justify-between p-5">
        <div className={`${tall ? 'text-[46px]' : 'text-[36px]'} font-black uppercase leading-[0.92] tracking-tight text-primary`}>Special end-of-year advertising deals!</div>
        {tall && <div className="space-y-1.5 text-[17px] font-bold"><p><span className="text-primary">1 · November is Thanksgiving</span> (gobble gobble)</p><p><span className="text-[hsl(var(--brand-green))]">2 · December is the Holiday Season</span>, plus the Red Hook Stars winners</p></div>}
        {!tall && <div className="text-[14px] font-bold">Thanksgiving + the Holiday Season. <span className="text-muted-foreground">The biggest selling season of the year.</span></div>}
        <Prices />
      </div>
      <Foot />
    </div>
  );
}

/** The end-of-year twofer deal as downloadable ads for Instagram, Facebook and email. */
export function TwoferAds() {
  const refs = useRef<Record<string, HTMLDivElement | null>>({});
  const [art, setArt] = useState(ART);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Load the artwork as embedded data so it is included when the image is saved.
  useEffect(() => {
    let on = true;
    fetch(ART).then((r) => r.blob()).then((b) => new Promise<string>((res) => { const fr = new FileReader(); fr.onload = () => res(String(fr.result)); fr.readAsDataURL(b); }))
      .then((d) => { if (on) setArt(d); }).catch((e) => console.error('twofer art', e));
    return () => { on = false; };
  }, []);
  const save = async (f: Format) => {
    const node = refs.current[f.id]; if (!node) return;
    setBusy(f.id); setError(null);
    try {
      const url = await toPng(node, { pixelRatio: 2, cacheBust: true });
      const a = document.createElement('a'); a.href = url; a.download = `star-revue-twofer-${f.id}.png`; a.click();
    } catch (e) { console.error(e); setError('That image could not be saved. Please try again.'); }
    finally { setBusy(null); }
  };
  return (
    <section className="mb-14">
      <h2 className="font-[family-name:var(--font-heading)] text-3xl font-semibold">End-of-year twofer ads</h2>
      <p className="mt-1 max-w-2xl text-sm text-muted-foreground">The Special Deals offer, sized for each place you'll post it. For email, add the image to your message and link it to the portal.</p>
      {error && <p role="alert" className="mt-3 text-sm text-destructive">{error}</p>}
      <div className="mt-6 space-y-10">{FORMATS.map((f) => (
        <div key={f.id} className="min-w-0">
          <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
            <h3 className="text-lg font-medium">{f.label} <span className="text-sm font-normal text-muted-foreground">· {f.note} · saves at {f.out}</span></h3>
            <button type="button" disabled={!!busy} onClick={() => void save(f)} className="inline-flex min-h-11 items-center gap-2 border border-foreground px-4 text-sm font-semibold disabled:opacity-50">
              {busy === f.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}Save image</button>
          </div>
          <AdFrame adId={`twofer-${f.id}`} label={f.label} capture={(el) => { refs.current[f.id] = el; }} className={`shrink-0 ${f.id === 'instagram' ? 'h-[540px] w-[540px]' : f.id === 'facebook' ? 'h-[315px] w-[600px]' : 'h-[760px] w-[600px]'}`}><Ad f={f} art={art} /></AdFrame>
        </div>))}</div>
    </section>
  );
}
